import React, { FC, FunctionComponent, useMemo, useState } from 'react';
import { Button, Loader, Modal, Card, Progress, Table, TextArea } from '@scuf/common';
import '@scuf/charts/honeywell/theme.css';
import './DataContextModal.scss';
import '@scuf/datatable/honeywell-combined/theme.css';
import { ERROR_MSG_FOR_USER_RESPONSE } from '../../constants/constants';
import { useDispatch, useSelector } from 'react-redux';
import {
  modelFeedback,
  getModelData,
  modelTableauDetails,
} from 'src/slices/contextSlice';

export interface IDataContextModalProps {
  onClose: () => void;
  open?: boolean;
  modalHeader?: string;
  questions?: any;
  uniqueId?: string;
  statgeId?: number;
  modelId?: number;
  isDemo?: boolean;
}

type QuestionDataProps = {
  question_id: number;
  question: string;
  options: string[];
  is_answered: boolean;
  preferable_resp?: string;
  tableau_link: string;
  user_response?: string;
};

const RenderCompleted: FC<{ show: boolean; questions: any[] }> = ({
  show,
  questions,
}) => {
  const answeredQuestions = questions.filter(({ is_answered }) => is_answered);
  if (!show) return null;
  return (
    <div className='answer-table'>
      <Table>
        <Table.Header>
          {['Question #', 'Question', 'Your Response'].map((item: string) => (
            <Table.HeaderCell content={item} key={item} />
          ))}
        </Table.Header>
        <Table.Body>
          {answeredQuestions.map((item: QuestionDataProps, i) => (
            <Table.Row key={item.question_id}>
              <Table.Cell>{i + 1}</Table.Cell>
              <Table.Cell>{item.question}</Table.Cell>
              <Table.Cell>{item.user_response}</Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table>
    </div>
  );
};
const RenderError: FC<{ show: boolean }> = ({ show }) => {
  if (!show) return null;
  return (
    <div data-testid='oppModal-error-text' className='oppModal--error-text'>
      {ERROR_MSG_FOR_USER_RESPONSE}
    </div>
  );
};

const RenderBody: FC<{
  questions: any[];
  question: string;
  tableau_link: string;
  loading: boolean;
  error: boolean;
  showFinished: boolean;
  totalDataAnswered: number;
  totalData: number;
}> = ({
  questions,
  tableau_link,
  loading,
  error,
  showFinished,

}) => {

    const RenderTableau = useMemo(
      () => (
        <>
          <div className='tableau-div'>
            <iframe
              title='context-modal'
              className={`tableau ${error ? 'show-error' : ''}`}
              src={tableau_link}
            />
          </div>
        </>
      ),
      [error, tableau_link]
    );

    return (
      <Card.Content className='contextModal--card '>
        <div className={`${!showFinished ? 'tableau__container' : ''}`}>
          {!loading && !showFinished && RenderTableau}
          <Loader loading={loading} text='Loading...' />
          <RenderCompleted show={showFinished} questions={questions} />
          <RenderError show={error}></RenderError>
        </div>
      </Card.Content>
    );
  };

export const DataContextModal: FunctionComponent<IDataContextModalProps> = (
  props
) => {
  const {
    questions,
    showFinished,
    loading: dataContextLoading,
    error: dataContextError,
    total_unique_keys,
    total_unique_keys_answered,
    next_unique_key,
    current_unique_id,
  } = useSelector(getModelData);

  const dispatch = useDispatch();

  const showNewRound =
    total_unique_keys !== total_unique_keys_answered + 1 && !!next_unique_key;

  const unAnsQuestions: QuestionDataProps[] = useMemo(
    () => questions.filter(({ is_answered }) => !is_answered),
    [questions]
  );

  const isAnsweredAll = useMemo(
    () => unAnsQuestions.length === 0,
    [unAnsQuestions.length]
  );

  const showCompleted = showFinished || isAnsweredAll;
  const currentQuestionData = unAnsQuestions.at(0);
  const displayCurrentQuestions = currentQuestionData?.question ?? '';
  const progressPercent = Number(
    ((total_unique_keys_answered / total_unique_keys) * 100).toFixed(1)
  );
  const submitAnswer = async (answer: any) => {
    const postBody = {
      question_id: currentQuestionData?.question_id,
      user_response: answer,
      unique_id: current_unique_id,
      is_demo: props.isDemo
    };
    dispatch(modelFeedback(postBody));
  };

  return (
    <Modal
      className={`modal-fi oppModal contextModal`}
      closeIcon={true}
      size={'large'}
      closeOnDimmerClick={false}
      closeOnDocumentClick={false}
      onClose={() => props.onClose()}
      open={props.open}>

      {

        dataContextLoading ? <Loader loading={dataContextLoading} text='Loading...' />
          :
          <Modal.Content className='contextModal--content'>
            <div className='contextModal--container'>

              {
                // !showCompleted &&
                <div className='contextModal--container--one'>
                  <div className='oppModal__heading contextModal--heading header-conatiner'>
                    {props.modalHeader}
                  </div>
                  <div className='question'>
                    {
                      displayCurrentQuestions && <div className='question--upper' >
                        {displayCurrentQuestions}
                      </div>
                    }
                    <div className='leftblock'   >

                      <BlockOptions
                        loading={dataContextLoading}
                        options={currentQuestionData?.options ?? []}
                        selectOption={submitAnswer}
                        onClose={props.onClose}
                        showFinished={showCompleted}
                        type={currentQuestionData?.type ?? ""}

                        onNewRound={() => {
                          props.isDemo ?
                            dispatch(modelTableauDetails({ unique_id: next_unique_key, model_id: props.modelId, stage_id: props.statgeId, is_demo: props.isDemo }))
                            :
                            dispatch(modelTableauDetails({ unique_id: next_unique_key, model_id: props.modelId, stage_id: props.statgeId }))

                        }
                        }
                        showNewRound={showNewRound}
                      />
                    </div>
                  </div>
                  <div
                    className='progress-conatiner'
                  >
                    <div className='progresstext'>
                      <div className='progresstext--currentpercentage'>{Math.round(progressPercent)}%</div>
                      <div className='progresstext--totalvalues'>{total_unique_keys_answered}/{total_unique_keys}</div>
                    </div>
                    <Progress
                      showProgress={false}
                      percent={Math.round(progressPercent)}
                      size='large'
                    />
                  </div>
                </div>
              }

              <div className='contextModal--container--two'>  <RenderBody
                loading={dataContextLoading}
                error={dataContextError}
                question={currentQuestionData?.question ?? ''}
                questions={questions}
                tableau_link={currentQuestionData?.tableau_link ?? ''}
                showFinished={!dataContextError && showCompleted}
                totalDataAnswered={total_unique_keys_answered}
                totalData={total_unique_keys}
              /></div>

            </div>

          </Modal.Content>
      }
    </Modal>
  );
};

const RenderCloseButton: FC<{
  onClose?: () => void;
  showNewRound: boolean;
  onNewRound?: () => void;
}> = ({ onClose, showNewRound, onNewRound }) => {
  return (
    <div
    //  className={`contextModal--answers ${!showNewRound ? 'flex-end' : ''}`}
    >
      {showNewRound && (
        <Button
          className={'button-space button-width'}
          size='small'
          type='primary'
          textTransform={false}
          onClick={onNewRound}>
          New Round
        </Button>
      )}

      <Button
        className={' button-space button-width'}
        size='small'
        type='secondary'
        textTransform={false}
        onClick={onClose}>
        Close
      </Button>
    </div>
  );
};

const BlockOptions: FC<{
  options: string[];
  selectOption: (answer: string) => void;
  loading: boolean;
  showFinished: boolean;
  onClose: () => void;
  showNewRound: boolean;
  onNewRound?: () => void;
  type: string;
}> = (props) => {
  const options = props.options;
  const responseType = props.type;
  const selectOption = props.selectOption;
  const loading = props.loading;
  const showFinished = !loading && props.showFinished;
  const [inputresponse, setInputresponse] = useState("");

  if (showFinished)
    return (
      <RenderCloseButton
        onClose={props.onClose}
        showNewRound={props.showNewRound}
        onNewRound={props.onNewRound}
      />
    );

  const handleInput = (value: any) => {
    setInputresponse(value);
  }


  return (
    <div className='contextModal--options'>
      {loading && <div>Loading...</div>}
      {!loading &&
        !showFinished && responseType === "options" &&
        options.map((option: string) => (
          <Button
            key={option}
            className={'button-space button-width'}
            size='small'
            type='primary'
            textTransform={false}
            onClick={() => selectOption(option)}>
            {option}
          </Button>
        ))}

      {
  !loading &&
        !showFinished && responseType === "input" && <> <TextArea
          className="input-response-text-box"
          value={inputresponse} onChange={handleInput}></TextArea>
          <Button
            className={'btn-text button-space button-width'} content='Submit'
            onClick={() => selectOption(inputresponse)}></Button>
        </>
      }

    </div>
  );
};
